/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../app/nls/StringResources',
	'../../lib/@waca/core-client/js/core-client/ui/core/Class'
],
function(_, stringResources, Class) {

	// TODO: to be removed when FF 'PropertiesV2' is enabled and widgets_en.properties is updated 
	const PROPERTY_BANNERS_TO_UPDATE = [{
		deprecatedValue: 'Text properties',
		newValue: 'Text'
	}, {
		deprecatedValue: 'Shape properties',
		newValue: 'Shape'
	}, {
		deprecatedValue: 'Image properties',
		newValue: 'Image'
	}, {
		deprecatedValue: 'Web page properties',
		newValue: 'Web page'
	}, {
		deprecatedValue: 'Media properties',
		newValue: 'Media'
	}];

	/**
	 * NOTE: All functions defined in widgetRegistry (other than initialize)
	 *		 begin with the _fn prefix to emphasize that they are functions not widget class keys.
	 */

	var WidgetRegistry = Class.extend({
		initialize: function(dashboardApi) {
			var collectionId = dashboardApi.getCollectionConfig('widget').id;
			const featureChecker = dashboardApi && dashboardApi.getGlassCoreSvc('.FeatureChecker');
			const isPropertiesV2Enabled = featureChecker && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled');

			return dashboardApi.findGlassCollection(collectionId).then(function(collection) {
				//TODO: Remove this when server has the filter widget contribution.
				// collection=collection.concat(this._fnHackInFilterWidgetForDevelopers());
				return this._fnInitializeRegistryItems(collection, isPropertiesV2Enabled).then(function() {
					//TODO: next step is to change the callers to use bluebird promises as well.
					return this;
				}.bind(this));
			}.bind(this)).fail((function(){
				throw stringResources.get('errorLoadingWidgetList');
			}).bind(this));
		},

		/**
		 * @returns a promise which resolves when all initialized widget registry items have been loaded.
		 */
		_fnInitializeRegistryItems: function(collection, isPropertiesV2Enabled) {
			if (!collection || !collection.length) {
				//No registry items to process.
				return Promise.resolve(collection);
			}

			if (isPropertiesV2Enabled) {
				this._updatePropertyLayouts(collection);
			}

			var promises = [];
			_.each(collection, function(item) {
				promises.push(this._fnInitializeRegistryItem(item, isPropertiesV2Enabled));
			}.bind(this));

			return Promise.all(promises);
		},

		/**  
		 *	TODO: when turn on FF 'PropertiesV2', the perspective in widgets.json, livewidget_core.json should be modified
		 *	and this function can be removed.
		 *	For now, this function updates the widget layout properties to make them aligned with Carbon design:
		 *	- rename section path that contains 'appearance' to 'positionAndStyle', 
		 *	- remove the group 'General'
		 *	- convert the section 'appearance' to 'postionAndStyle', 
		 *	- convert group 'webPage'/'media'/'image' to section 
		 *	- update the banner (remove the word 'properties' at the end of each banner label)
		 *	- change the uiControl type from 'Input' to 'InputLabel'
		*/
		_updatePropertyLayouts(collection) {
			collection.forEach(item => {
				let titleModeExist = false;
				if (item.propertyList) {
					item.propertyList.forEach(property => {
						if (property.editor) {
							if (property.editor.sectionId === 'general.appearance') {
								property.editor.sectionId = 'positionAndStyle';
							}
							if (property.editor.sectionId === 'visualization.chart') {
								property.editor.sectionId = 'chart';
							}
							if (property.editor.uiControl && property.editor.uiControl.type === 'Input') {
								property.editor.uiControl.type = 'InputLabel';
								property.editor.uiControl.inputType = 'text';
								property.editor.uiControl.label = property.editor.uiControl.placeHolderText;
							}
						}

						if (property.id === 'fillColor') {
							property.editor.position = 5;
						}
						if (property.id === 'borderColor') {
							property.editor.position = 6;
						}
						if (property.id === 'titleMode') {
							property.editor.sectionId = 'title';
							titleModeExist = true;
						}

					});
				}
				if (item.propertyLayoutList) {
					const itemsToRemove = [];
					item.propertyLayoutList.forEach((layout) => {
						if (layout.type === 'Group') {
							if (layout.id === 'general' || layout.id === 'visualization') {
								// Carbon design doesn't have group, put this item in the list to be removed
								itemsToRemove.push(layout.id);
							}
							if (['webPage', 'media', 'image'].indexOf(layout.id) > -1) {
								layout.type = 'Section';
								layout.position = 1;
								layout.label = stringResources.get('sectionName_details');
							}
						}

						if (layout.id === 'appearance') {
							layout.id = 'positionAndStyle';
							layout.label =stringResources.get('sectionName_positionAndStyle');
							layout.position = 200; // to put this section always at the last position
						}
						if (layout.type === 'Banner') {
							// Replace the deprecated banner with new value. 
							// Only update for English locale for now to know which FTs need to update when FF is turned on.
							const deprecatedTitle = PROPERTY_BANNERS_TO_UPDATE.find(banner => banner.deprecatedValue === layout.value);
							if (deprecatedTitle) {
								layout.value = deprecatedTitle.newValue;
							} else {
								// Case of 'Visualization properties': the banner should be the vis type and will be set in ViprProperties
								itemsToRemove.push(layout.id);
							}
						}

					});
					if (itemsToRemove.length) {
						item.propertyLayoutList = item.propertyLayoutList.filter(layout => itemsToRemove.indexOf(layout.id) < 0);
					}
					// Add section 'title' for the property 'titleMode'
					if (titleModeExist) {
						item.propertyLayoutList.push({
							type: 'Section',
							id: 'title',
							label: stringResources.get('titleTextLabel'),
							position: 190 // last section for live widget, just before section 'positionAndStyle'
						});
					}

				}
			});
		},

		/**
		 * @returns a promise which resolves when a single widgetRegistry entry has been loaded.
		 * 			In cases where a widgetRegistry entry has a custom model, the ModelClass is loaded.
		 */
		_fnInitializeRegistryItem: function(item, isPropertiesV2Enabled) {
			let type;
			// convert to local identifier for all built-in widgets
			if (item.builtin && item.id.indexOf('.') >= 0) {
				type = item.id.replace(/(\w+\.)/g, '');
			} else {
				type = item.id;
				this._fnInitCustomWidget(item, isPropertiesV2Enabled);
			}

			// default values for all widgets
			_.defaults(item, {
				layoutAuthoringView:'dashboard-core/js/dashboard/layout/authoring/views/Widget',
				disableTitle: false,
				ownTitle: true,
				createContentNode: true
			});

			// store the item to the widget registry
			this[type] = item;

			if (!item.modelClassPath || item.ModelClass) {
				//There is no modelClassPath in the widget definition or the ModelClass prototype has already been loaded.
				return Promise.resolve(item);
			}

			return this._fnLoadWidgetModelClass(item.modelClassPath)
				.then(function(ModelClass) {
					item.ModelClass = ModelClass;
					return item;
				});
		},

		_fnLoadWidgetModelClass: function(modelClassPath) {
			return new Promise(function(resolve, reject) {
				try {
					require([modelClassPath], resolve, reject);
				} catch(error) {
					reject(error);
				}
			});
		},

		_fnInitCustomWidget: function(widget, isPropertiesV2Enabled) {
			let sectionId, positionFillColor, positionBorderColor;
			if (isPropertiesV2Enabled) {
				sectionId = 'positionAndStyle';
				positionFillColor = 5;
				positionBorderColor = 6;
			} else {
				sectionId = 'general.appearance';
			}
			// default values for custom widgets
			widget = _.defaults(widget, {
				ownTitle: false,
				title: stringResources.get('customWidget'),
				layoutConsumeView: 'dashboard-core/js/dashboard/layout/views/Widget',
				svgIcon: 'dashboard-default-custom-widget',
				scroll: 'scroll', // [scroll | scrollNone | scrollX | scrollY],
				propertyLayoutList: [{
					id: 'banner',
					value: widget.title,
					name: 'banner',
					type: 'Banner'
				}],
				propertyList: [{
					id: 'fillColor',
					defaultValue: 'transparent',
					editor: {
						sectionId: sectionId,
						position: positionFillColor,
						uiControl: {
							type:'ColorPicker',
							label: stringResources.get('propFillColor'),
							showHexValue: true,
							addButton: true,
							open: false,
							ariaLabel: stringResources.get('propFillColor'),
							paletteType: 'DashboardColorSet'
						}
					}
				}, {
					id: 'borderColor',
					position: positionBorderColor,
					defaultValue: 'transparent',
					editor: {
						sectionId: sectionId,
						uiControl: {
							type: 'ColorPicker',
							label: stringResources.get('propBorderColor'),
							showHexValue: true,
							addButton: true,
							open: false,
							ariaLabel: stringResources.get('propBorderColor'),
							paletteType: 'DashboardColorSet'
						}
					}
				}]
			});

			// define a default spec for custom widget
			widget.defaultSpec = _.defaults(widget.defaultSpec || {}, {
				model: {
					type: widget.id,
					name: widget.ownTitle ? null : widget.title
				},
				layoutProperties: {
					style: {
						'display': 'flex',
						'border': '1px solid transparent',
						'border-radius': '5px',
						'flex-direction': 'column',
						'width':'250px',
						'height':'250px'
					}
				}
			});
		}
	});

	return WidgetRegistry;
});
